/**
 * Activity Service - Unified service for both web and mobile platforms
 * Handles activity log operations with filtering, sorting, and pagination
 */
import { extractItems } from '../utils/apiHelpers';
/**
 * Activity Service class
 * Singleton pattern per API client instance
 */
export class ActivityService {
    constructor(client) {
        this.client = client;
    }
    /**
     * Get or create singleton instance for the given API client
     */
    static getInstance(client) {
        if (!ActivityService.instances.has(client)) {
            ActivityService.instances.set(client, new ActivityService(client));
        }
        return ActivityService.instances.get(client);
    }
    /**
     * Clear all cached instances (useful for testing)
     */
    static clearInstances() {
        ActivityService.instances.clear();
    }
    /**
     * Get activities for a specific location
     *
     * @param locationId - Location UUID
     * @param params - Query parameters for filtering, sorting, and pagination
     * @returns Paginated list of activities
     */
    async getLocationActivities(locationId, params) {
        try {
            const queryParams = this.buildQueryParams(params);
            const response = await this.client.get(`/locations/${locationId}/activities`, queryParams);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch location activities');
            }
            return this.normalizeResponse(response.data, params);
        }
        catch (error) {
            console.error('[ActivityService] Get location activities error:', error);
            throw error;
        }
    }
    /**
     * Get activities for a specific account
     *
     * @param accountId - Account UUID
     * @param params - Query parameters for filtering, sorting, and pagination
     * @returns Paginated list of activities
     */
    async getAccountActivities(accountId, params) {
        try {
            const queryParams = this.buildQueryParams(params);
            const response = await this.client.get(`/accounts/${accountId}/activities`, queryParams);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch account activities');
            }
            return this.normalizeResponse(response.data, params);
        }
        catch (error) {
            console.error('[ActivityService] Get account activities error:', error);
            throw error;
        }
    }
    /**
     * Get all activities (super admin only)
     *
     * @param params - Query parameters for filtering, sorting, and pagination
     * @returns Paginated list of activities
     */
    async getAllActivities(params) {
        try {
            const queryParams = this.buildQueryParams(params);
            const response = await this.client.get('/activities', queryParams);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch all activities');
            }
            return this.normalizeResponse(response.data, params);
        }
        catch (error) {
            console.error('[ActivityService] Get all activities error:', error);
            throw error;
        }
    }
    /**
     * Get a single activity by ID
     *
     * @param activityId - Activity UUID
     * @returns Activity details
     */
    async getActivityById(activityId) {
        try {
            const response = await this.client.get(`/activities/${activityId}`);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch activity');
            }
            // Handle both { activity: {...} } and direct {...} response formats
            const data = response.data;
            const activity = data.activity || response.data;
            return this.normalizeActivity(activity);
        }
        catch (error) {
            console.error('[ActivityService] Get activity by ID error:', error);
            throw error;
        }
    }
    /**
     * Get activities for a specific resource (e.g., work order)
     *
     * @param locationId - Location UUID
     * @param resourceId - Resource UUID
     * @param params - Additional query parameters
     * @returns Paginated list of activities
     */
    async getActivitiesByResourceId(locationId, resourceId, params) {
        return this.getLocationActivities(locationId, {
            ...params,
            resource_id: resourceId,
        });
    }
    /**
     * Get activities by a specific user (actor)
     *
     * @param locationId - Location UUID
     * @param actorId - User UUID who performed the activities
     * @param params - Additional query parameters
     * @returns Paginated list of activities
     */
    async getActivitiesByActor(locationId, actorId, params) {
        return this.getLocationActivities(locationId, {
            ...params,
            actor_id: actorId,
        });
    }
    /**
     * Get activities by category
     *
     * @param locationId - Location UUID
     * @param categories - Activity categories to filter by
     * @param params - Additional query parameters
     * @returns Paginated list of activities
     */
    async getActivitiesByCategory(locationId, categories, params) {
        return this.getLocationActivities(locationId, {
            ...params,
            categories,
        });
    }
    /**
     * Build query parameters for the API request
     */
    buildQueryParams(params) {
        if (!params)
            return {};
        const queryParams = {};
        // Array parameters (comma-separated)
        if (params.activity_types?.length) {
            queryParams.activity_types = params.activity_types.join(',');
        }
        if (params.resource_types?.length) {
            queryParams.resource_types = params.resource_types.join(',');
        }
        if (params.categories?.length) {
            queryParams.categories = params.categories.join(',');
        }
        // UUID parameters
        if (params.resource_id) {
            queryParams.resource_id = params.resource_id;
        }
        if (params.actor_id) {
            queryParams.actor_id = params.actor_id;
        }
        // Date parameters (ISO 8601)
        if (params.from_date) {
            queryParams.from_date = params.from_date;
        }
        if (params.to_date) {
            queryParams.to_date = params.to_date;
        }
        // Pagination parameters
        if (params.limit !== undefined && params.limit > 0) {
            queryParams.limit = Math.min(params.limit, 1000); // Max 1000 per API spec
        }
        if (params.offset !== undefined && params.offset >= 0) {
            queryParams.offset = params.offset;
        }
        // Sorting parameters
        if (params.order_by) {
            queryParams.order_by = params.order_by;
        }
        if (params.order) {
            queryParams.order = params.order;
        }
        return queryParams;
    }
    /**
     * Normalize API response to standard format
     */
    normalizeResponse(data, params) {
        const responseData = data;
        // Extract activities array from various possible response formats
        const activities = extractItems(responseData, ['activities', 'items', 'data']);
        // Normalize each activity
        const normalizedActivities = activities.map((a) => this.normalizeActivity(a));
        // Extract pagination info
        const pagination = responseData.pagination;
        return {
            activities: normalizedActivities,
            total: responseData.total || pagination?.total || activities.length,
            count: responseData.count || activities.length,
            limit: responseData.limit || params?.limit || 50,
            offset: responseData.offset || params?.offset || 0,
            has_more: responseData.has_more ??
                (responseData.offset || 0) + activities.length < (responseData.total || activities.length),
        };
    }
    /**
     * Normalize a single activity object
     */
    normalizeActivity(activity) {
        return {
            id: activity.id,
            account_id: activity.account_id,
            location_id: activity.location_id,
            activity_type: activity.activity_type,
            category: activity.category,
            resource_type: activity.resource_type,
            resource_id: activity.resource_id,
            actor_id: activity.actor_id,
            actor_name: activity.actor_name,
            description: activity.description,
            metadata: activity.metadata,
            ip_address: activity.ip_address,
            user_agent: activity.user_agent,
            created_at: activity.created_at,
        };
    }
}
ActivityService.instances = new Map();
/**
 * Create activity service instance (Singleton per API client)
 */
export function createActivityService(apiClient) {
    return ActivityService.getInstance(apiClient);
}
// Default export for convenience
export default ActivityService;
